import { useTranslation } from "react-i18next";
import GitFitGridLoader from "@/components/GitFitGridLoader";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSession } from "@/providers/AppSessionProvider";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Users, TrendingUp, DollarSign, Calendar } from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

interface CreatorCode {
  id: string;
  name: string;
  code: string;
  commission_rate: number;
  is_active: boolean;
  created_at: string;
  total_signups: number;
  total_revenue_generated: number;
  total_paid_out: number;
  last_payout_date: string | null;
}

interface CreatorSignup {
  id: string;
  user_id: string;
  signup_date: string;
  is_premium: boolean;
  subscription_product_id: string | null;
  lifetime_revenue: number;
  monthly_revenue: number;
}

interface CreatorPayout {
  id: string;
  amount: number;
  payout_date: string;
  notes: string | null;
  signups_count: number;
  total_revenue_at_payout: number;
}

const CreatorDashboard = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { ready, session, isAuthenticated } = useAppSession();
  const [creatorCode, setCreatorCode] = useState<CreatorCode | null>(null);
  const [signups, setSignups] = useState<CreatorSignup[]>([]);
  const [payouts, setPayouts] = useState<CreatorPayout[]>([]);
  const [loading, setLoading] = useState(true);

  // Check authentication and load creator data
  useEffect(() => {
    if (ready !== 'ready') return;
    
    if (!isAuthenticated) {
      console.log("[CreatorDashboard] No session found, redirecting to auth");
      navigate("/auth", { replace: true });
      return;
    }

    fetchCreatorData();
  }, [ready, isAuthenticated, session, navigate]);

  const fetchCreatorData = async () => {
    try {
      setLoading(true);
      const userEmail = session?.user?.email;

      if (!userEmail) {
        toast.error(t('notifications.toast.no_email_found_in_session'));
        navigate("/", { replace: true });
        return;
      }

      // Fetch creator code for this email
      const { data: codeData, error: codeError } = await supabase
        .from('creator_referral_codes')
        .select('*')
        .eq('creator_email', userEmail)
        .single();

      if (codeError) {
        if (codeError.code === 'PGRST116') {
          // No creator code found for this email
          toast.error(t('notifications.toast.no_creator_account_found_for_your_email'));
          navigate("/", { replace: true });
          return;
        }
        throw codeError;
      }

      setCreatorCode(codeData);

      // Fetch signups for this creator code
      const { data: signupsData, error: signupsError } = await supabase
        .from('creator_referral_signups')
        .select('*')
        .eq('creator_code_id', codeData.id)
        .order('signup_date', { ascending: false });

      if (signupsError) throw signupsError;
      setSignups(signupsData || []);

      // Fetch payout history
      const { data: payoutsData, error: payoutsError } = await supabase
        .from('creator_payouts')
        .select('*')
        .eq('creator_code_id', codeData.id)
        .order('payout_date', { ascending: false });

      if (payoutsError) throw payoutsError;
      setPayouts(payoutsData || []);

    } catch (error) {
      console.error("[CreatorDashboard] Error fetching data:", error);
      toast.error(t('notifications.toast.failed_to_load_creator_data'));
    } finally {
      setLoading(false);
    }
  };

  const calculateOwedAmount = () => {
    if (!creatorCode) return 0;
    const totalCommission = creatorCode.total_revenue_generated * creatorCode.commission_rate;
    const owed = totalCommission - creatorCode.total_paid_out;
    return Math.max(0, owed);
  };

  const getSubscriptionLabel = (productId: string | null) => {
    if (!productId) return "Free";
    if (productId === "prod_TFVe92KUvlKJGg") return "Monthly Pro";
    if (productId === "prod_TFVe3ZSPeoxlVA") return "Annual Pro";
    if (productId === "lifetime") return "Lifetime";
    return "Premium";
  };

  if (ready === 'checking' || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <GitFitGridLoader className="mx-auto" />
          <p className="mt-4 text-muted-foreground">{t('ui.text.loading_your_creator_dashboard')}</p>
        </div>
      </div>
    );
  }

  if (!creatorCode) {
    return null;
  }

  return (
    <div className="container mx-auto py-8 px-4 max-w-7xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">{t('ui.text.creator_dashboard')}</h1>
        <p className="text-muted-foreground">Welcome back, {creatorCode.name}!</p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('ui.text.your_code')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              <code className="bg-muted px-3 py-1 rounded">{creatorCode.code}</code>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Status: {creatorCode.is_active ? (
                <Badge variant="outline" className="ml-1">Active</Badge>
              ) : (
                <Badge variant="destructive" className="ml-1">Inactive</Badge>
              )}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('ui.text.total_signups')}</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{creatorCode.total_signups}</div>
            <p className="text-xs text-muted-foreground mt-2">{t('ui.text.users_who_signed_up_with_your_code')}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('ui.text.total_revenue')}</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(creatorCode.total_revenue_generated)}</div>
            <p className="text-xs text-muted-foreground mt-2">
              Commission rate: {(creatorCode.commission_rate * 100).toFixed(0)}%
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('ui.text.amount_owed')}</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(calculateOwedAmount())}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {t('ui.text.total_paid')}: {formatCurrency(creatorCode.total_paid_out)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Signups */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>{t('ui.text.recent_signups')}</CardTitle>
        </CardHeader>
        <CardContent>
          {signups.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">{t('ui.text.no_signups_yet_share_your_code_to_get_started')}</div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('ui.text.signup_date')}</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>{t('ui.text.monthly_revenue')}</TableHead>
                    <TableHead>{t('ui.text.lifetime_revenue')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {signups.map((signup) => (
                    <TableRow key={signup.id}>
                      <TableCell>
                        {new Date(signup.signup_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {signup.is_premium ? (
                          <Badge className="bg-yellow-500">Premium</Badge>
                        ) : (
                          <Badge variant="outline">Free</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {getSubscriptionLabel(signup.subscription_product_id)}
                      </TableCell>
                      <TableCell>
                        {formatCurrency(signup.monthly_revenue)}/mo
                      </TableCell>
                      <TableCell>
                        {formatCurrency(signup.lifetime_revenue)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payout History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />{t('ui.text.payout_history')}</CardTitle>
        </CardHeader>
        <CardContent>
          {payouts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">{t('ui.text.no_payouts_recorded_yet')}</div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>{t('ui.text.signups_at_time')}</TableHead>
                    <TableHead>{t('ui.text.revenue_at_time')}</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payouts.map((payout) => (
                    <TableRow key={payout.id}>
                      <TableCell>
                        {new Date(payout.payout_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="font-semibold text-green-600">
                        {formatCurrency(payout.amount)}
                      </TableCell>
                      <TableCell>{payout.signups_count}</TableCell>
                      <TableCell>{formatCurrency(payout.total_revenue_at_payout)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {payout.notes || "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Help Text */}
      <div className="mt-6 text-sm text-muted-foreground text-center">
        <p>{t('ui.text.questions_about_your_payouts_contact_admin_at_aaro')}</p>
      </div>
    </div>
  );
};

export default CreatorDashboard;
